import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import type { ThreeEvent } from "@react-three/fiber";
import { useCallback, useEffect } from "react";
import { Mesh, Vector3 } from "three";
import type { Object3D } from "three";

import { GeneralLabel } from "../../components/common/general-label.js";
import { SeatedAvatarList } from "../../components/ui/office/avatars/seated-avatar-list.js";
import { UnseatedAvatarList } from "../../components/ui/office/avatars/unseated-avatar-list.js";
import { HoverBox } from "../../components/ui/office/hover-box.js";
import { useAvatarSeating } from "../../hooks/use-avatar-seating.js";
import { useCenteredGltf } from "../../hooks/use-centered-gltf.js";
import { useTableInteraction } from "../../hooks/use-table-interaction.js";
import { useContextMenuStore } from "../../store/context-menu.store.js";
import { useMyPresence } from "../presence/use-my-presence.js";

type GeneralTableProps = OfficePresetManifest["tables"][number] & {
  displayName: string;
  channelUuid: string;
  modelPath: string;
  tableId: string;
};

export const GeneralTable = ({
  modelPath,
  channelUuid,
  displayName,
  tableId,
  scale,
  position,
  rotation,
  hoverBox,
  text,
  seating,
}: GeneralTableProps) => {
  const { showContextMenu } = useContextMenuStore();
  const { groupRef, hovered, handleTableClick, table } = useTableInteraction({
    tableId,
  });
  const { updateAvailability } = useMyPresence();
  const { center, modelBottomY, modelHeight, clonedGltf } =
    useCenteredGltf(modelPath);

  const defaultStatus = (table?.defaultStatus as UserAvailability) ?? null;

  // Use the shared hook for user filtering and seat allocation
  // Pass the table node so the hook can subscribe to changes on this specific table
  const { seatedUsers, overflowUsers } = useAvatarSeating({
    getParticipantIds: () => [...(table?.participants ?? [])],
    locationNode: table,
    seatCount: seating?.seats?.length ?? 0,
  });

  useEffect(() => {
    const handleContextMenuEvent = (event: CustomEvent) => {
      if (event.detail.tableId !== tableId) {
        return;
      }

      showContextMenu({
        content: {
          channelUuid,
          displayName,
          tableId,
          type: "table",
        },
        worldPosition: new Vector3(
          event.detail.position[0],
          event.detail.position[1],
          event.detail.position[2]
        ),
      });
    };

    window.addEventListener(
      "table-context-menu",
      handleContextMenuEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "table-context-menu",
        handleContextMenuEvent as EventListener
      );
    };
  }, [channelUuid, displayName, showContextMenu, tableId]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) {
      return;
    }

    const clonedModel = clonedGltf();
    clonedModel.traverse((child: Object3D) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    // Position model locally at origin, offset only by center to center it
    clonedModel.rotation.set(0, 0, 0);
    clonedModel.position.set(-center.x, -center.y, -center.z);
    clonedModel.scale.set(...scale);
    group.add(clonedModel);

    return () => {
      group.remove(clonedModel);
    };
  }, [center, clonedGltf, groupRef, scale]);

  const moveTableAndSetDefaultStatus = useCallback(
    (event: ThreeEvent<MouseEvent | TouchEvent>) => {
      handleTableClick(event);

      if (defaultStatus) {
        updateAvailability(defaultStatus);
      }
    },
    [defaultStatus, updateAvailability, handleTableClick]
  );

  return (
    <group
      position={position as [number, number, number]}
      ref={groupRef}
      rotation={rotation as [number, number, number]}
      userData={{ tableId }}
    >
      <HoverBox
        config={hoverBox}
        hovered={hovered}
        modelBottomY={modelBottomY}
        modelHeight={modelHeight}
        onClick={moveTableAndSetDefaultStatus}
      />
      <GeneralLabel
        config={text}
        hovered={hovered}
        status={defaultStatus}
        value={displayName}
      />

      <SeatedAvatarList
        seats={(seating?.seats ?? []).map(
          (seat) => new Vector3(seat[0], seat[1], seat[2])
        )}
        users={seatedUsers}
      />

      <UnseatedAvatarList
        offset={
          new Vector3(
            ...(seating?.listOffset ?? seating?.seats?.at(-1) ?? [0, 0, 0])
          )
        }
        users={overflowUsers}
      />
    </group>
  );
};
